import { useEffect, useMemo, useState } from "react"
import { addWeeks, differenceInCalendarWeeks, differenceInSeconds, fromUnixTime, startOfWeek } from "date-fns"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import type { ILock } from "@/domains/lock"
import type { LockDisplayData } from "@/domains/lock-page/components/card/0.story-mocks"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { increaseLockDurationInteraction } from "@/domains/interactions"
import { IncreaseLockDurationActionsAdapter } from "@/domains/interactions/increase-lock-duration/increase-lock-duration-actions-adapter"
import { makeBreadcrumbs } from "@/domains/lock-page/breadcrumbs"
import { ExtendDurationCard } from "@/domains/lock-page/components/card/extend-duration-card/components/extend-duration-card"
import { LockPageLayout } from "@/domains/lock-page/components/lock-page-layout"
import { LockPageTabs } from "@/domains/lock-page/components/lock-page-tabs"
import { MANAGE_LOCK_TABS } from "@/domains/lock-page/constants"
import { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useSyncLockIdFromRouter } from "@/hooks/use-sync-lock-id-from-router"
import { useVestedToken } from "@/hooks/use-vested-token"
import useEventEmitter from "@/hooks/useEventEmitter"
import { clamp } from "@/lib/clamp"

export const epochMath = (numWeeks: number, now = new Date(Date.now() + new Date().getTimezoneOffset() * 60000)) => {
  const startOfEpoch = startOfWeek(now, { weekStartsOn: 4 })
  const nextEpoch = addWeeks(startOfEpoch, 1)
  const desiredEpoch = addWeeks(nextEpoch, numWeeks)
  const epochSecondsToExtend = differenceInSeconds(desiredEpoch, now) + 3600 // needs the extra time to put it in the epoch (not on the boundary)..
  const secondsToExtend = Math.min(epochSecondsToExtend, 7 * 26 * 86400)

  return {
    numWeeks,
    today: now,
    startOfEpoch,
    nextEpoch,
    desiredEpoch,
    epochSecondsToExtend,
    secondsToExtend,
  }
}

const ExtendLockDuration = observer(() => {
  const account = useAccount()
  const config = useConfig()
  const chainConfig = useChainConfig()
  const emitter = useEventEmitter()
  const vestedToken = useVestedToken()
  const scaleApp = useScaleApplication()
  const breadcrumbs = makeBreadcrumbs()

  useSyncLockIdFromRouter()

  const nft = scaleApp.Pages.Lock.selectedLock
  const nftId = nft?.id

  const [numWeeks, setNumWeeks] = useState(26)
  const [minWeeks, setMinWeeks] = useState(1)
  const nextEpochMath = epochMath(numWeeks)

  // Effect to update initial weeks when NFT data is loaded
  useEffect(() => {
    if (nft) {
      // Calculate current weeks from the same reference point as epochMath (next epoch)
      const now = new Date(Date.now() + new Date().getTimezoneOffset() * 60000)
      const startOfEpoch = startOfWeek(now, { weekStartsOn: 4 })
      const nextEpoch = addWeeks(startOfEpoch, 1)
      const currentWeeksFromNextEpoch = differenceInCalendarWeeks(fromUnixTime(nft.lockEnds), nextEpoch)

      // Allow slider to go to current duration (no extension) or extend from there
      const calculatedMinWeeks = Math.max(1, currentWeeksFromNextEpoch)
      setMinWeeks(calculatedMinWeeks)
      setNumWeeks(Math.min(calculatedMinWeeks, 26))
    }
  }, [nft])

  const potentialExtendedNft = useMemo(() => {
    if (!nft) {
      return scaleApp.Lock.Factory.createDefault()
    }

    // Calculate current weeks from the same reference point as epochMath (next epoch)
    const now = new Date(Date.now() + new Date().getTimezoneOffset() * 60000)
    const startOfEpoch = startOfWeek(now, { weekStartsOn: 4 })
    const nextEpoch = addWeeks(startOfEpoch, 1)
    const currentWeeksFromNextEpoch = differenceInCalendarWeeks(fromUnixTime(nft.lockEnds), nextEpoch)
    const newWeeks = numWeeks

    // If the slider is at or below current duration, return current state (no extension)
    if (newWeeks <= currentWeeksFromNextEpoch) {
      return scaleApp.Lock.Factory.createFromNft(nft)
    }

    // Calculate new lock ends timestamp from the desired epoch
    const newLockEnds = Math.floor(nextEpochMath.desiredEpoch.getTime() / 1000)

    // Calculate new lock value based on the extended duration
    // Voting power increases proportionally with duration
    const currentWeeksFloat = currentWeeksFromNextEpoch > 0 ? currentWeeksFromNextEpoch : 1
    const newWeeksFloat = newWeeks > 0 ? newWeeks : 1
    const durationRatio = newWeeksFloat / currentWeeksFloat
    const newLockValue = Number(nft.lockValue) * durationRatio

    return scaleApp.Lock.Factory.createFromNft({
      id: nft.id,
      lockAmount: nft.lockAmount,
      lockEnds: newLockEnds,
      lockValue: newLockValue,
    })
  }, [nft, numWeeks, nextEpochMath.desiredEpoch, scaleApp.Lock.Factory])

  // Check if there's actually an extension (future lock has later expiry than current)
  const hasExtension = potentialExtendedNft.lockEnds > (nft?.lockEnds ?? 0)
  const canExtendDuration = hasExtension && account.address !== undefined && nft !== null && !nft.hasExpired

  const onExtendLockDurationClick = async (numWeeks: number, nft?: ILock) => {
    if (!account.address || !nft) return

    const x = epochMath(numWeeks)
    const onChainActions = new IncreaseLockDurationActionsAdapter()

    const result = await increaseLockDurationInteraction({
      account: account.address,
      chainId: chainConfig.chainId,
      config,
      vestedAddress: vestedToken.address,
      tokenId: nft.id,
      secondsToExtend: x.secondsToExtend,
      emitter,
      eventBus: scaleApp.EventBus,
      onChainActions,
    })
  }

  if (!nft || !nftId) {
    return (
      <PageContainer>
        <p>not found</p>
      </PageContainer>
    )
  }

  const complexTab: ComplexTab | undefined = MANAGE_LOCK_TABS[1]

  if (!complexTab) {
    return null
  }

  const title = `Extend`

  // Pass ILock directly - TypeScript ensures type safety via Pick<ILock>
  const currentLockDisplayData = nft
  const futureLockDisplayData = potentialExtendedNft

  // Always provide a future lock (default to current lock if no extension) to prevent layout shift
  const futureLock = futureLockDisplayData ?? currentLockDisplayData

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle title={title} items={breadcrumbs} />
      <TooltipProvider>
        <LockPageLayout>
          <LockPageTabs />
          {currentLockDisplayData && nft && (
            <ExtendDurationCard
              nftId={nftId}
              currentLock={currentLockDisplayData}
              futureLock={futureLock}
              numWeeks={numWeeks}
              minWeeks={minWeeks}
              maxWeeks={26}
              currentExpiryDate={fromUnixTime(nft.lockEnds)}
              futureExpiryDate={fromUnixTime(futureLock?.lockEnds ?? nft.lockEnds)}
              vestedTokenSymbol={vestedToken.symbol}
              hasExpired={nft.hasExpired}
              canExtend={canExtendDuration}
              icon={complexTab.icon ? <complexTab.icon /> : undefined}
              onWeeksChange={(value: number) => {
                setNumWeeks(clamp(value, minWeeks, 26))
              }}
              onExtend={() => onExtendLockDurationClick(numWeeks, nft)}
            />
          )}
        </LockPageLayout>
      </TooltipProvider>
    </PageContainer>
  )
})

export default ExtendLockDuration
